import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { onAuthStateChange, signOutAdmin } from '../firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isAuthenticated: false,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // For admin portal security: Clear any persistent authentication on app initialization
    // This ensures that page refresh always requires re-authentication
    const initializeAuth = async () => {
      try {
        // Sign out any existing session silently
        await signOutAdmin();
      } catch (error) {
        // Ignore errors if no user was signed in
      }
      
      setUser(null);
      setLoading(false);
      
      // After clearing session, listen for new authentication events
      const unsubscribe = onAuthStateChange((authUser: User | null) => {
        setUser(authUser);
      });
      
      // Store unsubscribe function for cleanup
      return unsubscribe;
    };

    let unsubscribe: (() => void) | undefined;
    
    initializeAuth().then((unsub) => {
      unsubscribe = unsub;
    });

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};